import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import * as XLSX from "xlsx";
import { message, Select, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { BiSolidTrash, BiImport } from "react-icons/bi";
import { FaXmark } from "react-icons/fa6";
import { FaSearch } from "react-icons/fa";
import { HiOutlineDownload } from "react-icons/hi";
import { IoMdAdd } from "react-icons/io";
import { LuEye } from "react-icons/lu";
import { PiWarningCircleLight, PiExportBold } from "react-icons/pi";

import "./style.scss";
import { decryptData } from "../../../helpers/encrypt/crypto-encrypt";
import getBase64 from "../../../helpers/base64";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import ManageFilters from "../../../assets/component/manage-filters";
import CustomModal from "../../../assets/component/modal";
import CustomTable from "../../../assets/component/table";
import uris from "../../../config/uris";
import {
  createPlaybook,
  retrievePlaybook,
  createPlaybookReset,
  deletePlaybook,
  deletePlaybookReset,
  retrievePlaybookReset,
} from "../../../store/slices/manage-playbook/playbook";
import {
  importMasterQuestions,
  importMasterQuestionsReset,
} from "../../../store/slices/manage-playbook/questions";
import {
  exportQuestions,
  exportQuestionsReset,
} from "../../../store/slices/manage-playbook/exportQuestions";

const TableActions = ({
  playbookId,
  playbookName,
  setPlaybookName,
  setDeletePlaybookId,
  userRole,
  setShowDeletePlaybookModal,
}) => {
  const navigate = useNavigate();

  const handleDelete = (value, playbookName) => {
    setShowDeletePlaybookModal(true);
    setDeletePlaybookId(value);
    setPlaybookName(playbookName);
  };

  return (
    <div className="actions-container">
      <Tooltip title={"View"}>
        <div
          className="action-icons"
          onClick={() =>
            navigate(`${uris?.viewQuestions?.replace(":id", playbookId)}`)
          }
        >
          <span className="action-viewIcon">
            <LuEye />
          </span>
        </div>
      </Tooltip>
      {(["admin", "prompt_admin"].includes(userRole)) && <Tooltip title={"Delete"}>
        <div
          className="action-icons"
          onClick={() => {
            handleDelete(playbookId, playbookName);
          }}
        >
          <span className="action-delete-icon">
            <BiSolidTrash />
          </span>
        </div>
      </Tooltip>}
    </div>
  );
};

export const antIcon = (
  <LoadingOutlined style={{ fontSize: 18, color: "white" }} spin />
);

const AddPlaybook = ({ dispatch, createPlaybookLoading }) => {
  const [playbookName, setPlaybookName] = useState("");
  const [type, setType] = useState(null);
  const [visibility, setVisibility] = useState(null);
  const [fileName, setFileName] = useState("");
  const [base64File, setBase64File] = useState("");
  const [formErrors, setFormErrors] = useState({})
  const fileUploadRef = useRef(null);

  const visibilityOptions = [
    { value: "public", label: "Public" },
    { value: "private", label: "Private" },
  ];
  const typeOptions = [
    { value: "regulation", label: "Regulation" },
    { value: "benchmark", label: "Benchmark" },
    { value: "screening", label: "Screening" },
  ];

  const onPlaybookNameChange = (input, value) => {
    setPlaybookName(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [input]: ""
    }))
  };

  const onTypeChange = (input, value) => {
    setType(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [input]: ""
    }))
  };

  const onVisibilityChange = (input, value) => {
    setVisibility(value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [input]: ""
    }))
  };

  const handleCreatePlaybook = () => {
    if (createPlaybookLoading) return;
    let errors = {};
    if (!playbookName) {
      errors.playbookName = "Please enter the playbook name"
    }
    if (playbookName?.length > 50) {
      errors.playbookName = "Only 50 characters allowed"
    }
    if (!type) {
      errors.type = "Please select the type"
    }
    if (!visibility) {
      errors.visibility = "Please select the visibility"
    }
    if (!base64File) {
      errors.base64File = "Please upload the file"
    }
    setFormErrors(errors);
    if (Object.keys(errors).length === 0) {
      let payload = {
        playbookName: playbookName,
        type: type,
        visibility: visibility,
        questionsData: base64File,
      };

      dispatch(createPlaybook(payload));
    }
  };

  const handleFileChange = async (input, event) => {
    const file = event.target.files[0];
    event.target.value = "";    
    if (file) {
      const fileType = file.type;
      if (
        fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileType === "text/csv" ||
        fileType === "application/csv"
      ) {
        setFileName(file.name);
        setFormErrors((prevErrors) => ({
          ...prevErrors,
          [input]: ""
        }))
        try {
          const base64 = await getBase64(file);
          setBase64File(base64.split(";base64,")[1]);
        } catch (error) {
          message.error("Error converting file to base64:", error);
        }
      } else {
        message.error("Please select a valid .xlsx or .csv file");
      }
    }
  };

  const handleUploadFile = () => {
    if (fileUploadRef) {
      fileUploadRef.current.click();
    }
  };

  return (
    <div className="add-playbook-container">
      <div className="input-wrapper">
        <div className="input-label">
          Name <span>*</span>
        </div>
        <div className="add-playbook-wrapper">
          <input
            type="text"
            className="add-playbook-input"
            placeholder="Playbook Name"
            value={playbookName}
            onChange={(e) => onPlaybookNameChange("playbookName", e.target.value)}
          />
        </div>
        <div className="error-message">{formErrors.playbookName}</div>

      </div>
      <div className="input-wrapper">
        <div className="input-label">
          Type
          <span>*</span>
        </div>
        <div className="add-playbook-select-wrapper">
          <Select
            showSearch
            allowClear
            className="playbook-select"
            options={typeOptions}
            placeholder="Select Type"
            onChange={(e) => onTypeChange("type", e)}
            value={type}
            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
          />
        </div>
        <div className="error-message">{formErrors.type}</div>
      </div>
      <div className="input-wrapper">
        <div className="input-label">
          Visibility <span>*</span>
        </div>
        <div className="add-playbook-select-wrapper">
          <Select
            showSearch
            allowClear
            className="playbook-select"
            options={visibilityOptions}
            placeholder="Select Visibility"
            onChange={(e) => onVisibilityChange("visibility", e)}
            value={visibility}
            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
          />
        </div>
        <div className="error-message">{formErrors.visibility}</div>

      </div>
      <div className="input-wrapper">
        <div className="input-label">
          Questions <span>*</span>
        </div>
        <div className="upload-file" onClick={handleUploadFile}>
          <span className="choose-file">Choose File </span>
          <div className="file-name">
            {fileName ? fileName : "No File Chosen"}
          </div>
        </div>

        <input
          ref={fileUploadRef}
          type="file"
          accept=".xlsx"
          style={{ display: "none" }}
          onChange={(e) => handleFileChange("base64File", e)}
        />
        <div className="error-message">{formErrors.base64File}</div>

      </div>
      <div className="add-playbook-button-section">
        {createPlaybookLoading ? (
          <div className="button-wrapper create">
            <Spin indicator={antIcon} />
          </div>
        ) : (
          <div className="button-wrapper create" onClick={handleCreatePlaybook}>
            Create
          </div>
        )}
      </div>
    </div>
  );
};

export const ConfirmationModal = ({
  showModal,
  handleClose,
  message,
  handleConfirm,
}) => {
  return (
    showModal && (
      <CustomModal
        modalTitle={
          <div className="modal-title">
            <div className="modal-icon">
              <PiWarningCircleLight />
            </div>
            Confirm
          </div>
        }
        handleClose={handleClose}
        showCloseIcon
      >
        <>
          <p className="modal">{message}</p>
          <div className="action-buttons">
            <div className="button-wrapper yes" onClick={handleConfirm}>
              Yes
            </div>
            <div className="button-wrapper no" onClick={handleClose}>
              No
            </div>
          </div>
        </>
      </CustomModal>
    )
  );
};

const ManagePlaybook = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [showFilters, setShowFilters] = useState(true);
  const [showAddPlaybookModal, setShowAddPlaybookModal] = useState(false);
  const [showImportQuestionsModal, setShowImportQuestionsModal] = useState(false);
  const [deletePlaybookId, setDeletePlaybookId] = useState("");
  const [playbookName, setPlaybookName] = useState("");
  const [showDeletePlaybookModal, setShowDeletePlaybookModal] = useState(false);
  const [base64FileName, setBase64FileName] = useState("");
  const [importFileName, setImportFileName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [isSearchFilterApplied, setIsSearchFilterApplied] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [formattedTableData, setFormattedTableData] = useState({
    dataSource: [],
    columns: [],
  });

  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { isLoading: createPlaybookLoading, success: createPlaybookSuccess, error: createPlaybookError, data: playbookData } = useSelector((state) => state.createPlaybook);
  const { data: { playbookdata, entitiesData } = {} } = useSelector((state) => state.requestPlaybooks || {});
  const { data: retrievePlaybookdata, isLoading: retrievePlaybookLoading, error: retrievePlaybookError } = useSelector((state) => state.retrievePlaybook);
  const { success: importMasterQuestionsSuccess, error: importMasterQuestionsError, data: importMasterQuestionsData, isLoading: importMasterQuestionsLoading } = useSelector((state) => state?.importMasterQuestions);
  const { isLoading: exportQuestionsLoading, error: exportQuestionsError, success: exportQuestionsSuccess } = useSelector((state) => state?.exportQuestions);
  const { data: deletePlaybookData, error: deletePlaybookError } = useSelector((state) => state?.deletePlaybook);

  const menuData = {
    title: "Manage Playbook",
    parentMenu: {
      name: "ESG Playbooks",
      link: uris?.esgPlaybookRegulations,
    },
    subMenu: {
      name: "Manage Playbook",
      link: uris?.managePlaybook,
    },
  };

  const tableHeaderData = [
    {
      title: "Playbook",
      dataIndex: "playbookName",
      key: "playbookName",
      width: "25%",
      sorter: {
        compare: (a, b) => a.playbookName.localeCompare(b.playbookName),
        multiple: 3,
      },
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
      key: "visibility",
      width: "25%",
      sorter: {
        compare: (a, b) => a.visibilityRaw.localeCompare(b.visibilityRaw),
        multiple: 3,
      },
    },
    {
      title: "Count",
      dataIndex: "questionCount",
      key: "questionCount",
      width: "25%",
      sorter: {
        compare: (a, b) => a.questionCount - b.questionCount,
        multiple: 3,
      },
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      width: "25%",
    },
  ];

  const handleRetrievePlaybook = ({ type }) => {
    dispatch(retrievePlaybookReset())

    let payload = { page, limit, ...(isSearchFilterApplied && { searchValue }) };

    if (["SEARCH", "RESET"]?.includes(type)) {
      setPage(1);

      payload = {
        page: 1,
        limit,
        searchValue: (type === "SEARCH") ? searchValue : ""
      };
    }

    dispatch(retrievePlaybook(payload));
  };

  const handleSearch = (e) => {
    const value = e?.target?.value || "";
    setSearchValue(value);

    if (!value?.trim() && isSearchFilterApplied) {
      handleSearchClear();
    }
  }

  const handleKeyPress = (e) => {
    if (e?.key !== 'Enter' || e?.shiftKey) return;

    e?.preventDefault();
    const inputValue = e?.target?.value?.trim();

    if (inputValue) {
      handleRequestSearch();
    }
  }

  const handleSearchClear = () => {
    setSearchValue("")
    setIsSearchFilterApplied(false);
    handleRetrievePlaybook({ type: "RESET" })
  }

  const handleRequestSearch = () => {
    if (searchValue?.trim()) {
      setIsSearchFilterApplied(true);
      handleRetrievePlaybook({ type: "SEARCH" })
    }
  }

  const handleShowFiltersSection = () => {
    setShowFilters(!showFilters);
  };

  const handleImportFileChange = async (event) => {
    const file = event.target.files[0];
    event.target.value = "";

    if (file) {
      const fileType = file.type;
      if (
        fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        fileType === "text/csv" ||
        fileType === "application/csv"
      ) {
        setImportFileName(file.name);

        try {
          const base64 = await getBase64(file);

          setBase64FileName(base64.split(";base64,")[1]);

          setShowImportQuestionsModal(true);
        } catch (error) {
          message.error("Error converting file to base64:", error);
        }
      } else {
        message.error("Please select a valid .xlsx or .csv file");
      }
    }
  };

  const handleConfirmImport = () => {
    dispatch(importMasterQuestionsReset());

    setShowImportQuestionsModal(false);
    const payload = {
      questionsData: base64FileName,
    };
    dispatch(importMasterQuestions(payload));
  };

  const handleAddPlaybookModal = () => {
    setShowAddPlaybookModal(!showAddPlaybookModal);
  };

  const handleConfirmDelete = () => {
    setShowDeletePlaybookModal(false);
    const payload = {
      playbookId: deletePlaybookId,
    };
    dispatch(deletePlaybook(payload));
  };

  const handleExportMasterQuestions = () => {
    dispatch(exportQuestions());
  };

  const handleImportMasterQuestions = () => {
    if (fileInputRef) {
      fileInputRef.current.click();
    }
  };

  const handleDownloadTemplate = () => {
    const headers = [
      "DP Code",
      "Criteria",
      "Inclusion",
      "Exclusion",
      "Note",
      "Search Query",
      "Final Unit",
      "Data Type",
      "Parent DP Code",
      "Exclude Rule",
      "Null Reasoning"
    ];

    const rowData = [
      "ENVP006",
      "Retrieve only information about the company’s collaborations on environmental issues with government agencies, NGOs, or industry associations.",
      "1.Partnerships with government agencies, NGOs, or industry associations focused on environmental sustainability, climate change, pollution control, or resource conservation. 2.Names of partners and specific environmental initiatives or actions taken.",
      "1.Collaborations unrelated to environmental issues (e.g., social, economic, governance matters). 2.Future collaboration plans that are not yet implemented. 3.General mentions of partnerships without clear environmental focus. Internal collaborations with employees, suppliers, investors, or customers.",
      "Return only relevant details.",
      "Does the entity have a policy or policies on environmental issues?",
      "Yes/NA",
      "Number/Select/MultiSelect",
      "ENVP006.1",
      "Yes/No/1/0",
      "NA"
    ]

    const data = [headers, rowData];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "Questions_Template.xlsx");
  };

  useEffect(() => {
    if (page && limit) {
      handleRetrievePlaybook({ type: "PAGINATION" })
    }
  }, [page, limit])

  useEffect(()=>{
      const role_decrypt = decryptData(sessionStorage.getItem('eqlwoer'));
      setUserRole(role_decrypt?.role)
    },[])

  useEffect(() => {
    if (retrievePlaybookdata?.responseData) {

      const dataSource = (retrievePlaybookdata?.responseData || []).map((item) => ({
        ...item,
        key: item.id,
        visibilityRaw: item?.visibility,
        visibility: <div className="table-visibility">{item?.visibility}</div>,
        actions: (
          <TableActions
            playbookId={item?.id}
            playbookName={item?.playbookName}
            userRole={userRole}
            dispatch={dispatch}
            setPlaybookName={setPlaybookName}
            setDeletePlaybookId={setDeletePlaybookId}
            setShowDeletePlaybookModal={setShowDeletePlaybookModal}
          />
        ),
      }));

      setFormattedTableData({
        columns: tableHeaderData,
        dataSource,
      });
    } else {
      setFormattedTableData([])
    }
  }, [retrievePlaybookdata?.responseData])

  useEffect(() => {
    if (retrievePlaybookError?.message) {
      message?.error(retrievePlaybookError?.message)

      dispatch(retrievePlaybookReset())
    }
  }, [retrievePlaybookError?.message])

  useEffect(() => {
    if (exportQuestionsError) {
      message.error(exportQuestionsError?.message);
      dispatch(exportQuestionsReset());
    }
    if(exportQuestionsSuccess){
      message.success("Questions Exported Successfully");
      dispatch(exportQuestionsReset());
    }
  }, [exportQuestionsError, exportQuestionsSuccess]);

  useEffect(() => {
    if (createPlaybookSuccess) {
      message.success(playbookData.message);
      setShowAddPlaybookModal(false);
      handleRetrievePlaybook({ type: "RESET" });
    }

    if (createPlaybookError && createPlaybookError?.message) {
      message.error(createPlaybookError.message);
    }

    dispatch(createPlaybookReset());
  }, [createPlaybookSuccess, createPlaybookError, dispatch, playbookData.message]);

  useEffect(() => {
    if (deletePlaybookData) {
      message.success(deletePlaybookData?.message);
      dispatch(deletePlaybookReset());
      handleRetrievePlaybook({ type: "RESET" });
    }

    if (deletePlaybookError) {
      message.error(deletePlaybookError?.message);
      dispatch(deletePlaybookReset());
    }
  }, [deletePlaybookData, deletePlaybookError]);

  useEffect(() => {
    if (importMasterQuestionsSuccess) {
      message.success(importMasterQuestionsData.message);
    } else if (importMasterQuestionsError && importMasterQuestionsError?.message) {
      message.error(importMasterQuestionsError.message);
    }
    dispatch(importMasterQuestionsReset());
  }, [importMasterQuestionsData.message, importMasterQuestionsError, importMasterQuestionsSuccess]);

  return (
    <div className="manage-playbook-container">
      <Breadcrumbs breadcrumbDetails={menuData} />
      <div className="manage-playbook-filters">
        {showFilters && (
          <div className="show-filters-section">
            <div className="manage-playbook-filters-section">
              <div className="input-wrapper">
                <div className="input-container search-input-container">
                  <input
                    className="search-input"
                    value={searchValue}
                    onChange={(e) => handleSearch(e)}
                    onKeyDown={(e) => handleKeyPress(e)}
                    placeholder="Search playbook"
                  />
                  {searchValue && <FaXmark className="input-clear-icon" onClick={() => handleSearchClear()} />}
                  <div className="search-button" onClick={() => handleRequestSearch()}>
                    {retrievePlaybookLoading ? (
                      <Spin className="loading-icon" indicator={antIcon} />
                    ) : (
                      <FaSearch className="button-icon search-icon" />
                    )}
                  </div>
                </div>
              </div>
              <div className="filter-buttons-section content-buttons-left">
                <div
                  className="button-wrapper add-playbook"
                  onClick={() => handleAddPlaybookModal()}
                >
                  <IoMdAdd />  Create
                </div>
                <div
                  className="button-wrapper export-master-questions"
                  onClick={handleExportMasterQuestions}
                >
                  {exportQuestionsLoading ? <Spin className="export" indicator={antIcon} /> : <PiExportBold />}
                  Export Questions
                </div>
                <div
                  className="button-wrapper import-master-questions"
                  onClick={handleImportMasterQuestions}
                >
                  {importMasterQuestionsLoading ? <Spin className="import" indicator={antIcon} /> : <BiImport />}
                  Import Questions
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  style={{ display: "none" }}
                  onChange={(e) => handleImportFileChange(e)}
                />
                <div

                  className="button-wrapper questions-template"
                  onClick={handleDownloadTemplate}
                >

                  <HiOutlineDownload />
                  Template
                </div>
              </div>
            </div>
          </div>
        )}

        <ManageFilters
          showFilters={showFilters}
          handleShowFiltersSection={handleShowFiltersSection}
        />
      </div>

      <div className="manage-playbook-table-section">
        <CustomTable
          page={page}
          limit={limit}
          setPage={setPage}
          setLimit={setLimit}
          tableData={formattedTableData}
          totalRows={retrievePlaybookdata?.totalCount}
          isLoading={retrievePlaybookLoading}
        />
      </div>

      {/* Modals */}
      {showAddPlaybookModal && (
        <CustomModal
          handleClose={() => handleAddPlaybookModal()}
          showCloseIcon
          modalTitle="New Playbook"
        >
          <AddPlaybook
            playbookOptions={playbookdata}
            entitiesOptions={entitiesData}
            createPlaybookLoading={createPlaybookLoading}
            dispatch={dispatch}
          />
        </CustomModal>
      )}

      <ConfirmationModal
        showModal={showImportQuestionsModal}
        handleClose={() => setShowImportQuestionsModal(false)}
        message={
          <>
            Are you sure you want to import {" "}
            <strong>{importFileName ? importFileName : " "}</strong>?
          </>
        }
        handleConfirm={handleConfirmImport}
      />

      <ConfirmationModal
        showModal={showDeletePlaybookModal}
        handleClose={() => setShowDeletePlaybookModal(false)}
        message={
          <>
            Are you sure you want to delete {" "}
            <strong>{playbookName ? playbookName : " "}</strong> ?
          </>
        }
        handleConfirm={handleConfirmDelete}
      />
    </div>
  );
};

export default ManagePlaybook;


